import {
  useState,
} from 'react';
import {
  Callout,
  Field,
  Modal,
  Select,
  TextInput,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  explainFailure,
  formatDate,
  useLoad,
  SortTh,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  affiliateInvoiceAmounts,
  disputedAmount,
} from '../../screens.ts';
import { t } from '../../messages.ts';
import { Failure } from '../shared.tsx';

/**
 * Item 6 (third batch) — an affiliate's invoices, in the popup somebody has
 * open while that affiliate is on the phone asking about one. Read-only:
 * issuing and settling belong to the operator whose money it is.
 */
export function AffiliateInvoicesPanel({ api, affiliateId }: { api: PlatformApi; affiliateId: string }) {
  const loaded = useLoad(() => api.affiliateInvoices(affiliateId), [affiliateId]);

  return (
    <Panel title={t('aff.invoices.title')}>
      {loaded.state === 'loading' ? <Loading rows={1} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        loaded.value.data.length === 0 ? (
          <Empty>{t('aff.invoices.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{t('aff.invoices.colNumber')}</th><th>{t('common.status')}</th><th className="num">{t('aff.invoices.colTotal')}</th>
                  <th className="num">{t('aff.invoices.colSettled')}</th><th className="num">{t('aff.invoices.colDue')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.value.data.map((inv) => {
                  const money = affiliateInvoiceAmounts(inv);
                  return (
                    <tr key={inv.id}>
                      <td>{inv.invoice_number}</td>
                      <td><Chip tone={inv.status === 'PAID' ? 'locked' : 'provisional'}>{inv.status}</Chip></td>
                      <td className="num money">{money.total}</td>
                      <td className="num money">{money.settled}</td>
                      <td className="num faint">{inv.due_at ? formatDate(inv.due_at) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )
      ) : null}
    </Panel>
  );
}

/** Item 6 (third batch) — the same, for disputes. Read-only for the same reason. */
/**
 * §3.11 / E40 — asking this affiliate for authority, and using it.
 *
 * The one thing §3.9d's propose/approve handshake cannot do. A dispute is the
 * affiliate's claim against their operator, so routing it through the operator
 * for approval would hand the complaint to the party it is against. This asks
 * the affiliate instead, which is why it is a different mechanism rather than
 * another entry in the change-request catalogue.
 *
 * Nothing on this panel acts for the affiliate until they have granted a
 * mandate, and each mandate buys exactly one act.
 */
export function AffiliateMandatesPanel({ api, operatorId, affiliateId, canWrite }: {
  api: PlatformApi;
  operatorId: string;
  affiliateId: string;
  canWrite: boolean;
}) {
  const [version, setVersion] = useState(0);
  const loaded = useLoad(
    () => api.agentMandates(operatorId, affiliateId),
    [operatorId, affiliateId, version],
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [using, setUsing] = useState<string | null>(null);
  const [category, setCategory] = useState('COMMISSION_FIGURE');
  const [account, setAccount] = useState('');

  const refresh = () => setVersion((v) => v + 1);

  return (
    <Panel title={t('aff.mandates.title')}>
      {/* Batch 2 review, finding 7 — the server's own message after asking
          for or using a mandate; a result, read whole. */}
      {notice ? <Callout fold={false}>{notice}</Callout> : null}
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}

      {loaded.state === 'loading' ? <Loading rows={1} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        loaded.value.items.length === 0 ? (
          <Empty>{t('aff.mandates.empty')}</Empty>
        ) : (
          <Sorted rows={loaded.value.items} by={{ act: (m) => sortableCell(m.act), reason: (m) => sortableCell(m.reason), state: (m) => sortableCell(m.state), expires_at: (m) => sortableCell(m.expires_at) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="act">{t('aff.mandates.colFor')}</SortTh><SortTh sort={sort} col="reason">{t('aff.mandates.colWhy')}</SortTh><SortTh sort={sort} col="state">{t('common.state')}</SortTh>
                  <SortTh sort={sort} col="expires_at" align="num">{t('aff.mandates.colLapses')}</SortTh><th />
                </tr>
              </thead>
              <tbody>
                {ordered.map((m) => (
                  <tr key={m.id}>
                    <td>{m.act === 'RAISE_DISPUTE' ? t('aff.mandates.actRaiseDispute') : m.act}</td>
                    <td className="faint">{m.reason}</td>
                    <td>
                      <Chip
                        tone={
                          m.state === 'GRANTED'
                            ? 'locked'
                            : m.state === 'REQUESTED'
                              ? 'provisional'
                              : 'overdue'
                        }
                      >
                        {m.state}
                      </Chip>
                    </td>
                    <td className="num faint">{m.expires_at ? formatDate(m.expires_at) : '—'}</td>
                    <td>
                      {m.state === 'GRANTED' && canWrite ? (
                        <button onClick={() => { setFailure(null); setUsing(m.id); }}>
                          {t('aff.mandates.raiseTheDispute')}
                        </button>
                      ) : m.state === 'USED' ? (
                        <span className="faint">{t('aff.mandates.usedBy', { email: m.used_by_email ?? '—' })}</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )
      ) : null}

      {canWrite ? (
        <div className="form">
          <Field
            label={t('aff.mandates.askLabel')}
            hint={t('aff.mandates.askHint')}
          >
            <TextInput
              value={reason}
              onChange={setReason}
              placeholder={t('aff.mandates.askPlaceholder')}
            />
          </Field>
          <button
            className="primary"
            disabled={busy || reason.trim() === ''}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              void api
                .requestAgentMandate(operatorId, affiliateId, {
                  act: 'RAISE_DISPUTE',
                  reason: reason.trim(),
                })
                .then((r) => { setReason(''); setNotice(r.message); refresh(); })
                .catch((e: unknown) => setFailure(e))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? t('aff.mandates.asking') : t('aff.mandates.ask')}
          </button>
          <Callout tone="caution">
            {t('aff.mandates.askNote')}
          </Callout>
        </div>
      ) : null}

      {using ? (
        <Modal title={t('aff.mandates.raiseTitle')} onClose={() => setUsing(null)}>
          {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
          <Callout tone="caution">
            {t('aff.mandates.raiseNote')}
          </Callout>
          <div className="form">
            <Field label={t('aff.mandates.category')}>
              <Select
                value={category}
                onChange={setCategory}
                options={[
                  { value: 'COMMISSION_FIGURE', label: t('aff.mandates.catCommissionFigure') },
                  { value: 'ATTRIBUTION', label: t('aff.mandates.catAttribution') },
                  { value: 'DEDUCTION', label: t('aff.mandates.catDeduction') },
                  { value: 'CLAWBACK', label: t('aff.mandates.catClawback') },
                  { value: 'HOLD', label: t('aff.mandates.catHold') },
                  { value: 'SETTLEMENT', label: t('aff.mandates.catSettlement') },
                  { value: 'STATUS_CHANGE', label: t('aff.mandates.catStatusChange') },
                ]}
              />
            </Field>
            <Field
              label={t('aff.mandates.whatHappened')}
              hint={t('aff.mandates.whatHappenedHint')}
            >
              <TextInput value={account} onChange={setAccount} />
            </Field>
            <button
              className="primary"
              disabled={busy || account.trim() === ''}
              onClick={() => {
                setBusy(true);
                setFailure(null);
                void api
                  .raiseDisputeForAffiliate(operatorId, using, {
                    category,
                    what_happened: account.trim(),
                  })
                  .then((r) => {
                    setAccount('');
                    setUsing(null);
                    setNotice(r.message);
                    refresh();
                  })
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? t('aff.mandates.raising') : t('aff.mandates.raiseIt')}
            </button>
          </div>
        </Modal>
      ) : null}
    </Panel>
  );
}

export function AffiliateDisputesPanel({ api, affiliateId }: { api: PlatformApi; affiliateId: string }) {
  const loaded = useLoad(() => api.affiliateDisputes(affiliateId), [affiliateId]);

  return (
    <Panel title={t('aff.disputes.title')}>
      {loaded.state === 'loading' ? <Loading rows={1} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}
      {loaded.state === 'ready' ? (
        loaded.value.data.length === 0 ? (
          <Empty>{t('aff.disputes.empty')}</Empty>
        ) : (
          <Sorted rows={loaded.value.data} by={{ raised_at: (d) => sortableCell(d.raised_at), category: (d) => sortableCell(d.category), status: (d) => sortableCell(d.status), outcome: (d) => sortableCell(d.outcome), what_happened: (d) => sortableCell(d.what_happened) }}>
            {(ordered, sort) => (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <SortTh sort={sort} col="raised_at">{t('aff.disputes.colRaised')}</SortTh><SortTh sort={sort} col="category">{t('aff.disputes.colCategory')}</SortTh><SortTh sort={sort} col="status">{t('common.status')}</SortTh>
                  <SortTh sort={sort} col="outcome">{t('aff.disputes.colOutcome')}</SortTh><th className="num">{t('aff.disputes.colDisputed')}</th><SortTh sort={sort} col="what_happened">{t('aff.disputes.colWhat')}</SortTh>
                </tr>
              </thead>
              <tbody>
                {ordered.map((d) => (
                  <tr key={d.id}>
                    <td className="faint">{d.raised_at ? formatDate(d.raised_at) : '—'}</td>
                    <td>{d.category}</td>
                    <td><Chip tone={d.status === 'RESOLVED' ? 'locked' : 'provisional'}>{d.status}</Chip></td>
                    <td className="faint">{d.outcome ?? '—'}</td>
                    <td className="num money">{disputedAmount(d)}</td>
                    <td className="wrap">{d.what_happened}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
            )}
          </Sorted>
        )
      ) : null}
    </Panel>
  );
}
